import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Form, Input, Modal, Popconfirm, Select } from 'antd'
import { observer } from 'mobx-react-lite'
import StripeService from '../../../api/modules/api-stripe'
import { useStores } from '../../../cls/stores/MobXProvider'
import { stripeLoadUserSubscriptions } from '../../../stores/subscriptions/actions'
import { useSmartTranslation } from '../../../cls/TranslationContext'

const SubscriptionActions = ({
	subId,
	action,
	status,
	cancelAtPeriodEnd,
	style = {}
}) => {
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [form] = Form.useForm()
	const { users } = useStores()
	const { loggedInUser, loggedInUserId } = users
	const { t } = useSmartTranslation()
	// Feedback options for cancellation
	const feedbackOptions = [
		{
			value: 'customer_service',
			label: t('single.feedback.customer-service-was-bad')
		},
		{ value: 'low_quality', label: t('single.feedback.quality-less') },
		{
			value: 'missing_features',
			label: t('single.features.some-missing')
		},
		{ value: 'other', label: t('single.feedback.other') },
		{
			value: 'switched_service',
			label: t('single.feedback.switch-service')
		},
		{ value: 'too_complex', label: t('single.feedback.ease-fail') },
		{ value: 'too_expensive', label: t('single.feedback.expensive') },
		{ value: 'unused', label: t('single.feedback.not-using') }
	]

	// Initial form values for cancellation
	const initialFormValues = {
		cancellation_details: {
			comment: '',
			feedback: ''
		}
	}

	// Refresh subscriptions after an action
	const refreshSubscriptions = async () => {
		if (!loggedInUser || !loggedInUser.email) {
			console.error(
				'Logged-in user or user email is missing.',
				loggedInUser
			)
			return
		}
		await stripeLoadUserSubscriptions(loggedInUserId)
	}

	// Handle subscription cancellation
	const handleCancelSubscription = async (values) => {
		try {
			await StripeService.cancelSubscription(loggedInUser.email, {
				subId,
				...values
			})
			await refreshSubscriptions()
			setIsModalVisible(false)
			form.resetFields()
		} catch (error) {
			console.error('Error canceling subscription:', error)
		}
	}

	// Handle subscription resumption
	const handleResumeSubscription = async () => {
		try {
			await StripeService.resumeSubscription(loggedInUser.email, {
				subId
			})
			await refreshSubscriptions()
			setIsModalVisible(false)
		} catch (error) {
			console.error('Error resuming subscription:', error)
		}
	}

	// Render the modal for cancellation
	const renderCancelModal = () => (
		<Modal
			title={t('single.subscription.stop-renewal')}
			open={isModalVisible}
			onCancel={() => setIsModalVisible(false)}
			footer={null}
		>
			<Form
				form={form}
				layout="vertical"
				initialValues={initialFormValues}
				onFinish={handleCancelSubscription}
			>
				<Form.Item
					label={t('single.comment')}
					name={['cancellation_details', 'comment']}
				>
					<Input.TextArea rows={4} />
				</Form.Item>
				<Form.Item
					label="Feedback"
					name={['cancellation_details', 'feedback']}
					rules={[
						{
							required: true,
							message: t('single.feedback-prompt')
						}
					]}
				>
					<Select
						placeholder={t('single.feedback.select-type')}
						options={feedbackOptions}
					/>
				</Form.Item>
				<Form.Item>
					<Button type="primary" htmlType="submit">
						{t('single.confirm-stop-renewal')}
					</Button>
				</Form.Item>
			</Form>
		</Modal>
	)

	// 👇 keep your booleans clear
	const isCancelable = status === 'active' && !cancelAtPeriodEnd
	const isResumable = status === 'active' && cancelAtPeriodEnd

	// Single memoised computation
	const actionLabel = React.useMemo(() => {
		if (isResumable) return 'Resume Subscription'
		if (isCancelable) return 'Cancel Subscription'
		return null // nothing to show
	}, [isCancelable, isResumable])

	return (
		<div style={{ display: 'inline-block', ...style }}>
			{/* Render the action button */}
			{actionLabel && (
				<Button
					danger={isCancelable}
					type="primary"
					onClick={() => setIsModalVisible(true)}
				>
					{actionLabel}
				</Button>
			)}

			{/* Render the confirmation popups */}
			{action === 'resume' && (
				<Popconfirm
					title={t('single.subscription.resume-ask')}
					open={isModalVisible}
					onConfirm={handleResumeSubscription}
					onCancel={() => setIsModalVisible(false)}
					okType="link"
					placement="topLeft"
				/>
			)}

			{/* Render the cancellation modal */}
			{action === 'cancel' && renderCancelModal()}
		</div>
	)
}

SubscriptionActions.propTypes = {
	style: PropTypes.object,
	subId: PropTypes.string.isRequired,
	action: PropTypes.oneOf(['cancel', 'resume']).isRequired,
	status: PropTypes.string.isRequired,
	cancelAtPeriodEnd: PropTypes.bool
}

export default observer(SubscriptionActions)
